import { fetchComRetry } from './httpRetry';

const API_BASE = process.env.REACT_APP_AUTH_API_BASE || 'https://api.leveragesec.com.br';

export class AuthApiError extends Error {
  constructor(message, { status, blocked = false, fieldErrors = null } = {}) {
    super(message);
    this.name = 'AuthApiError';
    this.status = status;
    this.blocked = blocked;
    this.fieldErrors = fieldErrors;
  }
}

function toAuthApiError(status, data) {
  const detail = data && data.detail;
  if (Array.isArray(detail)) {
    const msg = detail.map(d => d && d.msg).filter(Boolean).join(' ');
    return new AuthApiError(msg || 'Dados inválidos.', { status, fieldErrors: detail });
  }
  const message = typeof detail === 'string'
    ? detail
    : status === 429
      ? 'Muitas tentativas. Aguarde um momento e tente novamente.'
      : 'Não foi possível completar a operação.';
  const blocked = status === 429 || /muitas tentativas/i.test(message);
  return new AuthApiError(message, { status, blocked });
}

// `retry`: usa fetchComRetry (absorve 502/503/504 passageiros — ver httpRetry.js)
// em vez de fetch cru. Só faz sentido pra chamadas sem risco de duplicar efeito
// ao repetir — login/guest apenas criam sessão, então é seguro. Endpoints com
// efeito colateral mais sensível (registro, redefinição de senha) continuam
// sem retry, mesmo padrão de internoApi.js.
async function request(method, path, body, { retry = false } = {}) {
  let res;
  try {
    // X-Requested-By: mitigação de CSRF exigida pelo backend em endpoints
    // autenticados por cookie SameSite=None (front e API em domínios
    // diferentes) — mesmo padrão já usado em internoApi.js. Vai em todo
    // POST, com ou sem corpo (ex: /auth/guest não manda corpo, mas o
    // backend exige o header do mesmo jeito).
    const headers = method === 'GET' ? undefined : { 'X-Requested-By': 'portal' };
    if (body) headers['Content-Type'] = 'application/json';
    const options = {
      method,
      credentials: 'include',
      headers,
      body: body ? JSON.stringify(body) : undefined,
    };
    res = await (retry ? fetchComRetry(`${API_BASE}${path}`, options) : fetch(`${API_BASE}${path}`, options));
  } catch (e) {
    throw new AuthApiError('Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.', { status: 0 });
  }
  let data = null;
  try { data = await res.json(); } catch (e) { /* sem corpo */ }
  if (!res.ok) throw toAuthApiError(res.status, data);
  return data;
}

const post = (path, body, opts) => request('POST', path, body, opts);
// GET não tem corpo — `opts` vai no 4º argumento. (Até 2026-09-22 ia no lugar
// do corpo: getMe() quebrava antes do fetch e todo F5 caía no /login mesmo com
// sessão válida.)
const get = (path, opts) => request('GET', path, undefined, opts);
const del = (path, opts) => request('DELETE', path, undefined, opts);

export function buildUser(usuario) {
  const nome = (usuario && usuario.nome) || '';
  const sobrenome = (usuario && usuario.sobrenome) || '';
  const email = (usuario && usuario.email) || '';
  const fullName = (nome + ' ' + sobrenome).trim() || email.split('@')[0] || 'Usuário';
  const iniciais = ((nome[0] || '') + (sobrenome[0] || '')).toUpperCase() || (email[0] || '?').toUpperCase();
  return {
    nome: fullName,
    iniciais,
    org: (usuario && usuario.empresa) || 'Conta individual',
    email,
    usuarioId: usuario && usuario.usuario_id,
    perfil: usuario && usuario.perfil,
    // Autorização real do Dash BTG — ver temAcessoBtgDashboard em NavBar.jsx.
    // Mesma chave nos dois formatos de resposta que alimentam `usuario` aqui:
    // aninhada em `usuario` no corpo de POST /auth/login (login(), abaixo) e
    // no nível raiz do corpo de GET /auth/me (getMe(), consumido direto como
    // `usuario` em App.jsx) — só site_user tem essa chave.
    temAcessoBtgDashboard: usuario && usuario.tem_acesso_btg_dashboard,
    // Projetos (dim_projeto_id) que este usuário vê — ver services/projetos.js.
    // Mesmas duas origens de tem_acesso_btg_dashboard; ausente = só Leverage.
    projetosVisiveis: (usuario && Array.isArray(usuario.projetos_visiveis)) ? usuario.projetos_visiveis : [1],
  };
}

export function registro({ nome, sobrenome, email, empresa, captcha_token }) {
  return post('/auth/registro', { nome, sobrenome, email, empresa: empresa || null, captcha_token });
}

export function confirmarCadastro({ email, codigo, senha }) {
  return post('/auth/confirmar-cadastro', { email, codigo, senha });
}

export function solicitarRedefinicao({ email, captcha_token }) {
  return post('/auth/solicitar-redefinicao', { email, captcha_token });
}

export function redefinirSenha({ email, codigo, nova_senha }) {
  return post('/auth/redefinir-senha', { email, codigo, nova_senha });
}

export function salvarPreferencias(dimEmissaoIds) {
  return post('/auth/preferencias', { dim_emissao_ids: dimEmissaoIds });
}

// Preferências de aviso por e-mail (arquivo novo publicado): matriz emissão
// favorita × tipo de arquivo. Uma linha por favorita, na mesma ordem de
// preferencias_emissao; as 4 chaves de tipo sempre vêm (sem acento:
// Documentos, Relatorios, Comunicados, Assembleia). Só site_user — convidado
// não tem essa rota. Formato devolvido (GET e POST):
//   { emissoes: [{ dim_emissao_id, tipos: { Documentos: bool, ... } }] }
export function getPreferenciasNotificacao() {
  return get('/auth/preferencias-notificacao');
}

// POST manda a matriz INTEIRA desejada — tipo omitido, false e emissão
// favorita fora do body viram NÃO marcado (0), então manda sempre todas as
// favoritas com os 4 tipos. Favorito novo nasce com os 4 ligados (desde
// 05/10/2026) — matriz velha na tela desligaria a emissão nova. 400:
// alguma emissão não é favorita (favoritos mudaram em outra aba); 422: tipo
// fora dos 4. Devolve a matriz atualizada, mesmo formato do GET.
export function salvarPreferenciasNotificacao(emissoes) {
  return post('/auth/preferencias-notificacao', { emissoes });
}

export function alterarSenha({ senha_atual, nova_senha }) {
  return post('/auth/alterar-senha', { senha_atual, nova_senha });
}

export async function atualizarPerfil({ nome, sobrenome, empresa }) {
  const data = await post('/auth/perfil', { nome, sobrenome, empresa: empresa || null });
  return { expiresIn: data.expires_in };
}

// Mesmo formato de resposta em /auth/login e /auth/continuar.
function toLoginResult(data) {
  return {
    expiresIn: data.expires_in,
    preferenciasEmissao: data.preferencias_emissao || [],
    user: buildUser(data.usuario),
  };
}

// Todo login registra este navegador pro "Continuar como" (cookie httpOnly de
// dispositivo lembrado). `lembrar` ("Lembrar de mim", campo `manter_conectado`,
// nome histórico) escolhe o nível: marcado, o portal continua sozinho quando a
// sessão expira; desmarcado, pergunta "Continuar como" e só vale enquanto o
// navegador estiver aberto.
export async function login({ email, senha, lembrar = false }) {
  const data = await post('/auth/login', { email, senha, manter_conectado: !!lembrar }, { retry: true });
  return toLoginResult(data);
}

// "Peek" do dispositivo lembrado: { nome, sobrenome, email, automatico } ou
// null. `automatico` = "Lembrar de mim" (continua sem perguntar). Não cria
// sessão. Qualquer falha (404, rede) vira null — no pior caso a pessoa vê o
// form de login normal, que é o comportamento de antes.
export async function buscarDispositivoLembrado() {
  try {
    return await get('/auth/dispositivo-lembrado');
  } catch (e) {
    return null;
  }
}

// "Continuar como <Nome>". SEM retry, de propósito: o backend rotaciona o token
// de dispositivo a cada uso, e um retry depois de a primeira resposta se perder
// reapresentaria o token já rotacionado (tratado como possível cópia do cookie).
// `automatico`: sem clique ("Lembrar de mim") — só muda a origem no monitor de
// acesso. `lembrar`: "Sim, e lembrar de mim" — não pergunta mais neste navegador.
export async function continuarLogado({ automatico = false, lembrar = false } = {}) {
  const body = (automatico || lembrar) ? { automatico, lembrar } : undefined;
  return toLoginResult(await post('/auth/continuar', body));
}

// "Não sou eu": esquece o dispositivo lembrado deste navegador.
export function esquecerDispositivo() {
  return del('/auth/dispositivo-lembrado');
}

let _meInFlight = null;
export function getMe() {
  if (!_meInFlight) {
    _meInFlight = get('/auth/me', { retry: true }).finally(() => { _meInFlight = null; });
  }
  return _meInFlight;
}

// `automatico`: sessão criada sem clique (link direto pra conteúdo público) —
// só muda a origem registrada no monitor de acesso (Auth.Sessoes.origem_sessao).
// Não chamar direto: cada POST abre uma sessão (1 acesso no monitor) — use
// garantirSessao(), que restaura antes de criar e deduplica chamadas simultâneas.
async function criarSessaoGuest({ automatico = false } = {}) {
  const data = await post('/auth/guest', automatico ? { automatico: true } : undefined, { retry: true });
  return { expiresIn: data.expires_in };
}

// Único ponto que pode criar sessão de convidado. Ordem: site_user (/auth/me)
// -> convidado já válido (GET /auth/guest) -> só então POST /auth/guest.
// Chamadas concorrentes (dois guards, StrictMode, vários 401 juntos) reusam a
// mesma promessa — duas criações simultâneas sem cookie viravam duas sessões.
// Devolve { tipo: 'site_user', usuario } ou { tipo: 'guest', criada, automatico }
// (`automatico` = origem da sessão, reaproveitada ou nova).
let _sessaoEmAndamento = null;
export function garantirSessao({ automatico = false } = {}) {
  if (!_sessaoEmAndamento) {
    _sessaoEmAndamento = restaurarOuCriarSessao(automatico)
      .finally(() => { _sessaoEmAndamento = null; });
  }
  return _sessaoEmAndamento;
}

async function restaurarOuCriarSessao(automatico) {
  try {
    return { tipo: 'site_user', usuario: await getMe() };
  } catch (e) { /* sem site_user — tenta convidado */ }
  const guest = await buscarSessaoGuest();
  if (guest) return { tipo: 'guest', criada: false, automatico: guest.origem_sessao === 'auto_link_publico' };
  await criarSessaoGuest({ automatico });
  return { tipo: 'guest', criada: true, automatico };
}

// Sessão de visitante ainda válida (cookie httpOnly) — { perfil, origem_sessao }
// ou null. /auth/me só reconhece site_user; sem isso todo F5 como convidado
// perdia a sessão.
export async function buscarSessaoGuest() {
  try {
    return await get('/auth/guest');
  } catch (e) {
    return null;
  }
}

export function logout() {
  return post('/auth/logout');
}

// Telemetria de navegação (monitor de acessos) — fire-and-forget: nunca deve
// bloquear a navegação nem propagar erro pro chamador (sem sessão válida
// ainda, por ex., é esperado e não é um caso a tratar no front).
export function registrarPageview(rota) {
  fetch(`${API_BASE}/auth/pageview`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'X-Requested-By': 'portal' },
    body: JSON.stringify({ rota }),
  }).catch(() => {});
}

// Beacon de encerramento de sessão, disparado no "pagehide" (fechar aba/
// navegador). sendBeacon não suporta headers customizados — por isso essa
// rota não exige X-Requested-By — e o cookie de sessão vai automático pelo
// navegador, sem precisar (e sem dar) configurar credentials aqui.
export function encerrarSessao() {
  navigator.sendBeacon(`${API_BASE}/auth/encerrar-sessao`);
}
